import {useEffect, useRef} from "react";
import {observer} from "mobx-react-lite";
import {useStores} from "stores/rootStore";
import {useAuth} from "hooks/useAuth";
import {paramsGet, paramsRemove} from "utils";
import {guestCheckoutSessionInfo, GuestCheckoutSessionInfo} from "requests";
import {getPendingGuestSession, setPendingGuestSession} from "guestCheckout";
import {guestClaimLoginCopy} from './loginCopy';

// Handles the return from a pay-first guest checkout. The anonymous user has
// already paid on Stripe; here we stash the session_id (so the subsequent auth
// request carries it) and open the auth modal pre-filled with the Stripe email
// so they can claim/verify the account. The backend moves the billing onto the
// durable account during signup/login (reconcile_guest_billing).
//
// Authenticated returns are handled by SubscriptionStore.handleStripeReturn.
export const GuestCheckoutReturnHandler = observer(() => {
    const root = useStores();
    const {user} = root;
    const {login} = useAuth();
    const startedRef = useRef(false);

    useEffect(() => {
        if (startedRef.current) return;
        if (!user.initialized) return; // wait until anon/auth state is resolved

        const stripe = paramsGet('stripe');
        const success = paramsGet('success');
        const sessionId = paramsGet('session_id');
        const isClaim = !!paramsGet('claim');
        if (!stripe || !success || !sessionId) return;
        // Anonymous → claim via the auth modal. Logged-in user who opened the
        // claim email (`claim` flag) → attach to the current account directly.
        // A normal authenticated checkout return (no claim flag) is handled by
        // SubscriptionStore.handleStripeReturn.
        if (!user.isAnonymous && !isClaim) return;

        startedRef.current = true;
        const clearParams = () => paramsRemove('stripe', 'success', 'payment_type', 'session_id', 'claim');
        (async () => {
            if (user.isAnonymous) {
                let info: GuestCheckoutSessionInfo | null = null;
                try {
                    info = await guestCheckoutSessionInfo(sessionId);
                } catch { /* email is a nice-to-have; the claim still works without it */ }
                // Stashed with what was actually sold: the login on the far side has to
                // say what the claim did, and a trial is the one checkout that can come
                // back with nothing — the account signed in with may have had its own
                // already, and the backend cancels this one then.
                const pending = getPendingGuestSession();
                const context = info ?? (pending?.sessionId === sessionId ? pending : null);
                setPendingGuestSession(sessionId, !!context?.trial, context ?? undefined);
                clearParams();
                login({
                    prefillEmail: info?.email || undefined,
                    forceMode: info?.account_exists ? 'login' : 'signup',
                    // Resist accidental dismissal — a stray click right after
                    // paying must not drop the claim flow (the explicit close
                    // button still works). Re-openable via the subscription-page
                    // CTA (PendingGuestClaim) if they do close it.
                    persistent: true,
                    // The purchase is already paid — no free-plan promise here.
                    reason: 'purchase',
                    copy: guestClaimLoginCopy(context),
                });
            } else {
                clearParams();
                await root.subscription.claimGuestCheckoutReturn(sessionId);
            }
        })();
    }, [user.initialized, user.isAnonymous, login, root.subscription]);

    return null;
});
